import { ReactNode, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  appContextTypeAtom,
  communityLoadingAtom,
  communityErrorAtom,
} from '@/lib/stores/community';
import { useAuth } from '@/lib/hooks/useAuth'; // Assuming a custom hook for auth state
import { Spinner } from '@/components/ui/atoms/spinner';
import { routerLogger } from '@/lib/logger';
import type { SubdomainType } from '@/lib/utils/subdomain';
import { isResetInProgress } from '@/lib/services/atomicReset';

/** Props for ProtectedRoute */
interface ProtectedRouteProps {
  children?: ReactNode; // Make children optional if using Outlet
  requiredContext: SubdomainType | 'app' | 'any'; // Context needed ('platform', 'community', 'app' or 'any')
  requiredRole?: 'admin' | 'owner' | 'member' | 'employer'; // Updated roles
}

/**
 * AI Context: ProtectedRoute Component
 *
 * Purpose: Wraps routes to enforce application context (platform/community)
 *          and authentication/authorization.
 * Location: src/components/features/routing/ProtectedRoute.tsx
 * Usage: Used as a wrapper element in router definitions (Routes.tsx).
 *        Renders an <Outlet /> for nested routes or {children}.
 */
const ProtectedRoute = ({
  children,
  requiredContext,
  requiredRole,
}: ProtectedRouteProps) => {
  const location = useLocation();
  const { user, loading: authLoading, session, hasRole } = useAuth();

  // Get application context state from Jotai
  const contextType = useAtomValue(appContextTypeAtom);
  const communityLoading = useAtomValue(communityLoadingAtom);
  const communityError = useAtomValue(communityErrorAtom);

  // Updated loading check - ensure we wait for all necessary data
  // Also suspend navigation during cache resets to prevent errors
  const isLoading =
    authLoading ||
    contextType === 'loading' ||
    (contextType === 'community' && communityLoading) ||
    // If we need role checking, wait for user profile to be loaded
    (requiredRole && user && !user.profile) ||
    // Phase 2: Suspend navigation during atomic resets
    isResetInProgress();

  useEffect(() => {
    // Log context/auth state for debugging route protection
    if (!isLoading) {
      const actualRoles = user?.profile?.role || 'none';
      routerLogger?.debug(
        {
          pathname: location.pathname,
          requiredContext,
          actualContext: contextType,
          contextLoading: communityLoading,
          contextError: communityError,
          authLoading,
          isAuthenticated: !!user,
          requiredRole,
          actualRoles, // Now shows actual roles instead of 'TODO'
        },
        'ProtectedRoute check'
      );
    }
  }, [
    isLoading,
    location.pathname,
    requiredContext,
    contextType,
    communityLoading,
    communityError,
    authLoading,
    user,
    requiredRole,
  ]);

  // --- Loading State ---
  if (isLoading) {
    // Show different message during cache reset
    const isResetting = isResetInProgress();
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">
            {isResetting ? 'Refreshing cache...' : 'Loading context...'}
          </p>
        </div>
      </div>
    );
  }

  // --- Context Check ---
  // Check if the current context matches the required context
  if (requiredContext !== 'any' && contextType !== requiredContext) {
    routerLogger?.warn(
      {
        required: requiredContext,
        actual: contextType,
        path: location.pathname,
      },
      'Context mismatch, redirecting'
    );

    // For context mismatches, redirect to root landing as a fallback
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // --- Community Error Check ---
  // If community context was required but resulted in an error (e.g., not found)
  if (contextType === 'community' && communityError) {
    routerLogger?.error(
      { error: communityError, path: location.pathname },
      'Community context error, redirecting to error page'
    );
    // Redirect to a dedicated error page
    return (
      <Navigate to="/community-not-found" state={{ from: location }} replace />
    );
  }

  // --- Authentication Check ---
  if (requiredRole && !user) {
    routerLogger?.warn(
      { path: location.pathname, requiredRole },
      'Authentication required, redirecting to login'
    );
    // Redirect to the main app login page (/login)
    // Community-specific login (/login within community context) is handled by its own route definition.
    // const loginPath = contextType === 'platform' ? '/login' : '/login'; // Simplified
    const loginPath = '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // --- Authorization Check (Role Check) ---
  // If a specific role is required, check if the user has it.
  // Only check authorization after all data is loaded
  if (
    requiredRole &&
    user &&
    user.profile && // Ensure profile is loaded
    !authLoading &&
    !hasRole(requiredRole)
  ) {
    const actualRoles = user.profile.role || 'none';
    routerLogger?.warn(
      { path: location.pathname, requiredRole, actualRoles }, // Now shows actual roles
      'Authorization failed, redirecting to unauthorized'
    );
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  // --- Render Content ---
  // If all checks pass, render the child component (page) or the Outlet for nested routes.
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
